import { Injectable } from '@angular/core';
import { CalendarEventInfo } from '@mommy/models/CalendarEventInfo.model';
import { ReminderService } from '@mommy/services/reminder/reminder.service';
import { UtilService } from '@mommy/services/util.service';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import * as _ from 'lodash';
import {
  CreateEvent,
  CreateEventsBatch,
  DeleteEvent,
  GetEventDetail,
  LoadEventsByRange,
  UpdateEvent,
} from './reminder.actions';

export interface ReminderStateModel {
  loading: boolean;
  events: CalendarEventInfo[]; // 已載入的事件（跨多個區間累積）
  error: string | null;
}

const defaultReminderState = (): ReminderStateModel => ({
  loading: false,
  events: [],
  error: null,
});

@State<ReminderStateModel>({
  name: 'ReminderState',
  defaults: defaultReminderState(),
})
@Injectable()
export class ReminderState {
  constructor(
    private reminderService: ReminderService,
    private util: UtilService
  ) {}

  //#region ======================== Selector ========================
  @Selector([ReminderState])
  static loading(state: ReminderStateModel): boolean {
    return state.loading;
  }

  @Selector([ReminderState])
  static events(state: ReminderStateModel): CalendarEventInfo[] {
    return state.events;
  }

  /** 依 event_id 取得單筆事件 */
  static eventById(eventId: number) {
    return createSelector([ReminderState], (state: ReminderStateModel) =>
      _.find(state.events, { event_id: eventId })
    );
  }
  //#endregion

  //#region ======================== Action ========================
  @Action(LoadEventsByRange)
  async loadEventsByRange(
    ctx: StateContext<ReminderStateModel>,
    { from, to, event_type, silent }: LoadEventsByRange
  ) {
    ctx.patchState({ loading: true, error: null });
    try {
      const fetched = await this.reminderService.getEvents({
        from,
        to,
        event_type,
      });
      const state = ctx.getState();
      // 以區間為單位覆蓋：移除落在 [from, to] 的舊資料，換上本次抓回的，避免已刪除事件殘留
      const outside = state.events.filter(
        (e) => !(e.event_date >= from && e.event_date <= to)
      );
      ctx.patchState({
        loading: false,
        events: this.dedupeById([...outside, ...fetched]),
      });
    } catch (error) {
      console.error('[Reminder] LoadEventsByRange error', error);
      ctx.patchState({ loading: false, error: '提醒載入失敗' });
      // 背景載入（首頁角標）失敗不打擾使用者
      if (!silent) {
        this.util.showErrorWithToash('提醒載入失敗，請稍後再試');
      }
    }
  }

  @Action(GetEventDetail)
  async getEventDetail(
    ctx: StateContext<ReminderStateModel>,
    { eventId }: GetEventDetail
  ) {
    try {
      const event = await this.reminderService.getEvent(eventId);
      this.upsertEvents(ctx, [event]);
    } catch (error) {
      console.error('[Reminder] GetEventDetail error', error);
    }
  }

  @Action(CreateEvent)
  async createEvent(
    ctx: StateContext<ReminderStateModel>,
    { dto }: CreateEvent
  ) {
    const created = await this.reminderService.createEvent(dto);
    this.upsertEvents(ctx, [created]);
  }

  @Action(CreateEventsBatch)
  async createEventsBatch(
    ctx: StateContext<ReminderStateModel>,
    { dtos }: CreateEventsBatch
  ) {
    const created = await Promise.all(
      dtos.map((dto) => this.reminderService.createEvent(dto))
    );
    this.upsertEvents(ctx, created);
  }

  @Action(UpdateEvent)
  async updateEvent(
    ctx: StateContext<ReminderStateModel>,
    { eventId, dto }: UpdateEvent
  ) {
    const updated = await this.reminderService.patchEvent(eventId, dto);
    this.upsertEvents(ctx, [{ ...updated, event_id: updated.event_id ?? eventId }]);
  }

  @Action(DeleteEvent)
  async deleteEvent(
    ctx: StateContext<ReminderStateModel>,
    { eventId }: DeleteEvent
  ) {
    await this.reminderService.deleteEvent(eventId);
    const state = ctx.getState();
    ctx.patchState({
      events: state.events.filter((e) => e.event_id !== eventId),
    });
  }
  //#endregion

  // ====== 工具 ======

  /** 將事件 upsert 進 state（依 event_id 置換或新增） */
  private upsertEvents(
    ctx: StateContext<ReminderStateModel>,
    incoming: CalendarEventInfo[]
  ) {
    const state = ctx.getState();
    ctx.patchState({
      events: this.dedupeById([...state.events, ...incoming]),
    });
  }

  /** 依 event_id 去重，後者覆蓋前者 */
  private dedupeById(events: CalendarEventInfo[]): CalendarEventInfo[] {
    const map = new Map<number, CalendarEventInfo>();
    for (const e of events) {
      if (e && e.event_id != null) {
        map.set(e.event_id, e);
      }
    }
    return Array.from(map.values());
  }
}
